import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';

import { useAuth } from '@/contexts/AuthContext';
import { Alert } from '@/components/ui';
import { Logo } from '@/components/Logo';
import { ApiError } from '@/lib/api';
import { getGoogleClientId } from '@/lib/google-client-id';

/**
 * Tela de Login — Rodada Frente A (2026-05-17).
 *
 * Botão principal: "Entrar com Google" (skeleton no backend até Matheus
 * configurar Google Cloud project). Se o backend retornar 501, mostra aviso
 * claro pro usuário.
 *
 * Em dev (NODE_ENV !== production), expõe formulário "dev-login" pra desbloquear
 * desenvolvimento sem dependência de Google. Aceita qualquer email do domínio
 * autorizado (@igmsecurity.com.br).
 *
 * Aviso fixo: acesso restrito a colaboradores @igmsecurity.com.br.
 *
 * FASE-C: a caixa de erro era uma `<div>` desenhada à mão (fora do design
 * system) e duas mensagens vazavam vocabulário interno — "Resposta Google sem
 * credential" e o texto que o servidor devolvia em `message`. Agora é o `Alert`
 * canônico e todas as frases são de tela, não de exceção.
 */
export function LoginPage() {
  const { isAuthenticated, loginDev, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const googleClientId = getGoogleClientId();

  const [devEmail, setDevEmail] = useState('matheus@igmsecurity.com.br');
  const [devNome, setDevNome] = useState('');
  const [loadingDev, setLoadingDev] = useState(false);
  const [error, setError] = useState('');

  // Detecta dev a partir do Vite mode (PROD/DEV) — backend também valida
  const isDev = import.meta.env.DEV;

  if (isAuthenticated) {
    return <Navigate to="/" />;
  }

  function navigateNext() {
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
    navigate(from, { replace: true });
  }

  async function handleGoogleSuccess(credentialResponse: CredentialResponse) {
    setError('');
    const credential = credentialResponse.credential;
    if (!credential) {
      setError('O Google não concluiu o login. Tente de novo.');
      return;
    }
    try {
      await loginWithGoogle(credential);
      navigateNext();
    } catch (err) {
      if (err instanceof ApiError) {
        const responseError = err.response as { error?: string; message?: string } | undefined;
        if (err.status === 403 && responseError?.error === 'domain-not-allowed') {
          setError('Esta conta não é do domínio da IGM. Entre com o seu e-mail @igmsecurity.com.br.');
        } else if (err.status === 501) {
          setError('O login com Google ainda não está ligado neste ambiente. Avise o administrador.');
        } else {
          setError('Não foi possível entrar agora. Tente de novo em instantes.');
        }
      } else {
        setError('Não foi possível entrar com o Google. Tente de novo em instantes.');
      }
    }
  }

  function handleGoogleError() {
    setError(
      'Falha ao autenticar com o Google. Confira se está usando uma conta @igmsecurity.com.br.'
    );
  }

  async function handleDevSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!devEmail.trim()) {
      setError('Informe o e-mail.');
      return;
    }
    setLoadingDev(true);
    try {
      await loginDev({ email: devEmail.trim(), nome: devNome.trim() || undefined });
      navigateNext();
    } catch (err) {
      if (err instanceof ApiError) {
        const responseError = (err.response as { error?: string; message?: string } | undefined);
        if (err.status === 403 && responseError?.error === 'domain-not-allowed') {
          setError('Esta conta não é do domínio da IGM. Use um e-mail @igmsecurity.com.br.');
        } else if (err.status === 404) {
          setError('Este atalho não está disponível neste ambiente.');
        } else {
          setError('Não foi possível entrar agora. Tente de novo em instantes.');
        }
      } else {
        setError('Não foi possível entrar agora. Tente de novo em instantes.');
      }
    } finally {
      setLoadingDev(false);
    }
  }

  return (
    <div className="min-h-screen bg-paper dark:bg-paper-dark flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Logo centralizado */}
        <div className="mb-12 flex justify-center">
          <Logo size="lg" />
        </div>

        {/* Título */}
        <h1 className="font-display text-4xl text-ink-1 dark:text-ink-dark-1 tracking-tight mb-2">
          Bem-vindo de volta
        </h1>
        <p className="mb-6 text-sm text-ink-2 dark:text-ink-dark-2">
          Acesso restrito a colaboradores <strong>@igmsecurity.com.br</strong>.
        </p>
        {/* Quem chega aqui procurando o portal do condomínio precisa de rota —
            senão fica tentando entrar com um e-mail que nunca vai ser aceito. */}
        <p className="-mt-3 mb-6 text-sm text-ink-2 dark:text-ink-dark-2">
          É cliente da IGM?{' '}
          <a
            href="/cliente/login"
            className="font-medium text-red-deep hover:underline dark:text-red-glow"
          >
            Entre no Portal do Cliente
          </a>
          .
        </p>
        {/* 25/10/2026: o técnico sem e-mail da empresa entra com o e-mail pessoal e a senha do convite. */}
        <p className="-mt-3 mb-6 text-sm text-ink-2 dark:text-ink-dark-2">
          Técnico sem e-mail da empresa?{' '}
          <a
            href="/campo/entrar"
            className="font-medium text-red-deep hover:underline dark:text-red-glow"
            data-testid="login-link-tecnico"
          >
            Entre com e-mail e senha
          </a>
          .
        </p>

        {error && (
          <Alert variant="danger" rail className="mb-5">
            {error}
          </Alert>
        )}

        {/* Login Google — real se Client ID configurado, fallback informativo se não */}
        {googleClientId ? (
          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              text="signin_with"
              shape="pill"
              theme="outline"
              size="large"
              width="320"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() =>
              setError(
                'O login com Google ainda não está ligado neste ambiente. Avise o administrador.'
              )
            }
            className="w-full h-11 rounded-xl bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark text-ink-1 dark:text-ink-dark-1 font-medium hover:border-red transition-colors flex items-center justify-center gap-2"
          >
            <GoogleGlyph />
            <span>Entrar com Google</span>
          </button>
        )}

        {/* Em dev: form abaixo */}
        {isDev && (
          <>
            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-line dark:bg-line-dark" />
              <span className="text-xs font-semibold uppercase tracking-widest text-ink-3 dark:text-ink-dark-3">
                Só em desenvolvimento
              </span>
              <div className="h-px flex-1 bg-line dark:bg-line-dark" />
            </div>

            <form onSubmit={handleDevSubmit} className="space-y-3" autoComplete="off">
              <div>
                <label
                  htmlFor="devEmail"
                  className="block text-xs font-medium text-ink-2 dark:text-ink-dark-2 mb-1"
                >
                  E-mail (precisa ser @igmsecurity.com.br)
                </label>
                <input
                  id="devEmail"
                  type="email"
                  required
                  value={devEmail}
                  onChange={(e) => setDevEmail(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1 placeholder-ink-3 border border-line dark:border-line-dark focus:border-red focus:ring-2 focus:ring-red-soft dark:focus:ring-red-soft-dark focus:outline-none transition-colors text-sm"
                  placeholder="seu@igmsecurity.com.br"
                />
              </div>
              <div>
                <label
                  htmlFor="devNome"
                  className="block text-xs font-medium text-ink-2 dark:text-ink-dark-2 mb-1"
                >
                  Nome (opcional — só pra primeiro login)
                </label>
                <input
                  id="devNome"
                  type="text"
                  value={devNome}
                  onChange={(e) => setDevNome(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1 placeholder-ink-3 border border-line dark:border-line-dark focus:border-red focus:ring-2 focus:ring-red-soft dark:focus:ring-red-soft-dark focus:outline-none transition-colors text-sm"
                  placeholder="Seu Nome"
                />
              </div>
              <button
                type="submit"
                disabled={loadingDev}
                className="w-full h-10 rounded-xl bg-red hover:bg-red-deep text-white font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-sm"
              >
                {loadingDev ? 'Entrando…' : 'Entrar (dev)'}
              </button>
              <p className="mt-2 text-center text-xs text-ink-2 dark:text-ink-dark-2">
                Este atalho existe só no ambiente de desenvolvimento. Em produção, o acesso é
                exclusivamente pelo Google.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function GoogleGlyph() {
  // SVG mínimo do "G" do Google. Ícone simplificado pra não trazer outro asset.
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}
